import { useEffect, useState } from "react";
import StatusBadge from "@/components/feature/StatusBadge";
import { pipelineStages, sideStages, stageHints } from "@/mocks/prospectos";
import type { Prospect } from "@/pages/prospectar/components/ProspectCard";
import {
  fetchReassignTargets,
  fetchTimeline,
  flagNeedsHuman,
  reassignProspect,
  resolveNeedsHuman,
  type ReassignTarget,
  type TimelineEvent,
} from "@/services/pipeline";
import ContactThread from "./ContactThread";

const ICON: Record<string, string> = {
  created: "ri-user-add-line",
  opener: "ri-send-plane-line",
  message: "ri-chat-1-line",
  reply: "ri-chat-check-line",
  ai_reply: "ri-robot-2-line",
  link_click: "ri-cursor-line",
  registered: "ri-slideshow-3-line",
  stage: "ri-flag-line",
  needs_human: "ri-alarm-warning-line",
  resolved: "ri-check-double-line",
  reassigned: "ri-share-forward-line",
  wa_invalid: "ri-error-warning-line",
};
const TONE: Record<string, string> = {
  needs_human: "bg-accent-500/15 text-accent-700",
  wa_invalid: "bg-accent-500/15 text-accent-700",
  resolved: "bg-primary-500/15 text-primary-700",
  stage: "bg-secondary-500/15 text-secondary-700",
};
const REL: Record<string, string> = { sponsor: "Tu patrocinador", equipo: "Tu equipo", admin: "Equipo Vívelo" };

type Props = {
  prospect: Prospect | null;
  onClose: () => void;
  onMove: (id: string, status: string) => void;
  onPatch: (id: string, patch: Partial<Prospect>) => void;
  onReassigned: (id: string) => void;
  notify: (m: string) => void;
};

export default function ContactDrawer({ prospect, onClose, onMove, onPatch, onReassigned, notify }: Props) {
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [targets, setTargets] = useState<ReassignTarget[] | null>(null);
  const [toId, setToId] = useState("");
  const [busy, setBusy] = useState(false);
  const [showDerive, setShowDerive] = useState(false);
  const id = prospect?.id;

  useEffect(() => {
    setShowDerive(false);
    setToId("");
    setTargets(null);
    if (!id) return;
    let on = true;
    fetchTimeline(id).then((e) => on && setEvents(e)).catch(() => on && setEvents([]));
    return () => {
      on = false;
    };
  }, [id, prospect?.status, prospect?.needsHuman]);

  if (!prospect) return null;
  const p = prospect;
  const isWa = p.platform === "WhatsApp";

  const resolve = async () => {
    setBusy(true);
    try {
      await resolveNeedsHuman(p.id);
      onPatch(p.id, { needsHuman: false, needsHumanReason: undefined, handledBy: "human" });
      notify("Marcado como atendido");
    } catch {
      notify("No se pudo actualizar");
    } finally {
      setBusy(false);
    }
  };

  const flag = async () => {
    setBusy(true);
    try {
      await flagNeedsHuman(p.id, "Lo marcaste para atenderlo personalmente");
      onPatch(p.id, { needsHuman: true, needsHumanReason: "Lo marcaste para atenderlo personalmente", handledBy: "human" });
    } catch {
      notify("No se pudo actualizar");
    } finally {
      setBusy(false);
    }
  };

  const openDerive = async () => {
    setShowDerive(true);
    if (targets) return;
    try {
      setTargets(await fetchReassignTargets());
    } catch {
      setTargets([]);
    }
  };

  const derive = async () => {
    if (!toId) return;
    setBusy(true);
    try {
      await reassignProspect(p.id, toId);
      notify("Contacto derivado");
      onReassigned(p.id);
      onClose();
    } catch (e) {
      notify(e instanceof Error ? e.message : "No se pudo derivar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true" aria-label={`Ficha de ${p.name}`}>
      <div className="absolute inset-0 bg-black/50" onClick={onClose}></div>
      <aside className="relative w-full max-w-lg bg-background-50 h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 z-10 bg-background-50/95 backdrop-blur border-b border-background-200 px-5 py-4 flex items-center gap-3">
          <img src={p.photo} alt={`Foto de ${p.name}`} className="w-11 h-11 rounded-xl object-cover object-top border border-background-200" />
          <div className="min-w-0 flex-1">
            <div className="font-heading font-bold text-foreground-950 truncate">{p.name}</div>
            <div className="text-xs text-foreground-500 truncate">
              {p.username} · {p.platform}
            </div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-background-200 cursor-pointer" aria-label="Cerrar">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="p-5 space-y-5">
          {p.needsHuman ? (
            <div className="rounded-2xl border border-accent-400/50 bg-accent-500/10 p-4">
              <div className="flex items-start gap-2 text-sm text-accent-800">
                <i className="ri-alarm-warning-line text-lg mt-0.5"></i>
                <div className="flex-1">
                  <div className="font-semibold">Necesita tu intervención</div>
                  <div className="text-xs mt-0.5">{p.needsHumanReason ?? "Revisa la conversación y responde tú."}</div>
                </div>
              </div>
              <button disabled={busy} onClick={() => void resolve()} className="mt-3 px-4 py-2 rounded-full text-sm font-semibold bg-primary-500 text-background-950 hover:bg-primary-600 disabled:opacity-40 cursor-pointer">
                Ya lo atendí
              </button>
            </div>
          ) : (
            <button disabled={busy} onClick={() => void flag()} className="text-xs font-semibold text-foreground-600 hover:text-accent-700 cursor-pointer">
              <i className="ri-alarm-warning-line mr-1"></i>Marcar que quiero atenderlo yo
            </button>
          )}

          <section>
            <h3 className="font-heading font-bold text-sm text-foreground-950 mb-2">Estado en tu pipeline</h3>
            <div className="flex items-center gap-3">
              <StatusBadge status={p.status} size="md" />
              <select
                value={p.status}
                onChange={(e) => onMove(p.id, e.target.value)}
                aria-label="Cambiar estado"
                className="flex-1 bg-background-100 border border-background-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-primary-400 cursor-pointer"
              >
                {[...pipelineStages, ...sideStages].map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <p className="text-[11px] text-foreground-500 mt-1.5">{stageHints[p.status] ?? ""}</p>
            <div className="mt-3 flex items-center gap-1" aria-hidden="true">
              {pipelineStages.map((s, i) => {
                const cur = pipelineStages.indexOf(p.status);
                return <span key={s} className={`h-1.5 flex-1 rounded-full ${cur >= i ? "bg-primary-500" : "bg-background-300"}`} title={s}></span>;
              })}
            </div>
          </section>

          <section>
            <h3 className="font-heading font-bold text-sm text-foreground-950 mb-2">Cómo ha avanzado</h3>
            {events.length ? (
              <ol className="relative border-l border-background-300 ml-3 space-y-3">
                {events.map((e) => (
                  <li key={e.id} className="ml-5">
                    <span className={`absolute -left-3 w-6 h-6 rounded-full flex items-center justify-center text-xs ${TONE[e.kind] ?? "bg-primary-500/15 text-primary-700"}`}>
                      <i className={ICON[e.kind] ?? "ri-checkbox-circle-line"}></i>
                    </span>
                    <div className="text-sm text-foreground-900">{e.label}</div>
                    <div className="text-[11px] text-foreground-500">{new Date(e.createdAt).toLocaleString("es", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</div>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="text-sm text-foreground-500">Aún no hay movimientos.</p>
            )}
          </section>

          <section>
            <h3 className="font-heading font-bold text-sm text-foreground-950 mb-2">Conversación</h3>
            <ContactThread prospectId={p.id} isWhatsApp={isWa} notify={notify} />
          </section>

          <section>
            <h3 className="font-heading font-bold text-sm text-foreground-950 mb-2">Derivar</h3>
            {!showDerive ? (
              <button onClick={() => void openDerive()} className="px-4 py-2 rounded-full text-sm font-semibold border border-background-300 text-foreground-800 hover:bg-background-200 cursor-pointer">
                <i className="ri-share-forward-line mr-1"></i>Derivar a otra persona del equipo
              </button>
            ) : targets === null ? (
              <div className="text-sm text-foreground-500">Cargando…</div>
            ) : targets.length === 0 ? (
              <p className="text-sm text-foreground-500">No hay personas disponibles para derivar todavía.</p>
            ) : (
              <div className="flex gap-2">
                <select value={toId} onChange={(e) => setToId(e.target.value)} className="flex-1 bg-background-100 border border-background-200 rounded-xl px-3 py-2 text-sm outline-none cursor-pointer" aria-label="Persona a la que derivar">
                  <option value="">Elige a quién…</option>
                  {targets.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name} · {REL[t.relation] ?? t.relation}
                    </option>
                  ))}
                </select>
                <button disabled={!toId || busy} onClick={() => void derive()} className="px-4 py-2 rounded-full text-sm font-semibold bg-primary-500 text-background-950 disabled:opacity-40 cursor-pointer">
                  Derivar
                </button>
              </div>
            )}
            <p className="text-[11px] text-foreground-500 mt-1.5">Al derivarlo, el contacto y su conversación pasan a esa persona y dejan de aparecer en tu lista.</p>
          </section>
        </div>
      </aside>
    </div>
  );
}
